import React, { useState } from "react";
import {
  CopyIcon,
  DropdownIcon,
  DropUpIcon,
} from "../../../assets/icon/IconCollection";
import useCopyToClipboard from "../../hooks/copyToClipboard";
import useExtractRoute from "../../hooks/useExtractRoute";
import "./RequestResponseViewer.scss";

export interface Title {
  titleName: string;
  httpMethod?: HttpMethod;
}
export enum HttpMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DEL",
  PATCH = "PATCH",
}

export enum WidgetType {
  Webhook = "WebHook",
  ApiRequestResponse = "ApiRequestResponse",
}

interface Props {
  isCollapsable: boolean;
  title?: Title;
  widgetType: WidgetType;
  webhookResponseJson?: Record<string, any>;
  curlRequestCommand?: string;
  curlResponseCommand?: Record<string, any>;
  url?: string;
  method?: string
}

const RequestResponseViewer: React.FC<Props> = (props) => {
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("request");

  const handleCollapseToggle = () => {
    setCollapsed(!collapsed);
  };

  return (
    <div style={{ maxWidth: '100%' }}>
      <div className="widget">
        <WidgetHeader
          title={props.title}
          url={useExtractRoute(props.url ?? '')}
          method={props.method as HttpMethod}
          isCollapsable={props.isCollapsable}
          collapsed={collapsed}
          handleCollapseToggle={handleCollapseToggle}
        ></WidgetHeader>
        {!collapsed && (
          <>
            {props.widgetType === WidgetType.ApiRequestResponse && (
              <TabContainer
                widgetProps={props}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              ></TabContainer>
            )}
            <CodeDisplay
              widgetProps={props}
              activeTab={activeTab}
            ></CodeDisplay>
          </>
        )}
      </div>
    </div>
  );
};

const WidgetHeader: React.FC<{
  title?: Title;
  url?: string;
  method?: HttpMethod;
  isCollapsable: boolean;
  collapsed: boolean;
  handleCollapseToggle: () => void;
}> = (props) => {
  const getColor = (httpMethod: HttpMethod) => {
    switch (httpMethod) {
      case "GET":
        return "#355ABF"; // Green
      case "POST":
        return "#1F8747"; // Blue
      case "PUT":
        return "#D36D13"; // Amber
      case "DEL":
        return "#AE1B6A"; // Red
    }
  };

  return (
    <>
      <div className="response-header">
        {props.method && <span className="widegt-header-method" style={{ color: getColor(props.method) }}>{props.method}</span>}
        {props.title ? props.title.titleName : props.url}
        {props.isCollapsable && (
          <span className="collapse-icon" onClick={props.handleCollapseToggle}>
            {props.collapsed ? <DropUpIcon /> : <DropdownIcon />}
          </span>
        )}
      </div>
    </>
  );
};

const TabContainer: React.FC<{
  widgetProps: Props;
  activeTab: string;
  setActiveTab: (tab: string) => void;
}> = (props) => {
  if (
    props.widgetProps.curlRequestCommand === undefined ||
    props.widgetProps.curlResponseCommand === undefined
  ) {
    throw new Error("curlRequestCommand or curlResponseCommand is undefined");
  }

  const [copied, copyToClipboard] = useCopyToClipboard();
  const [isCopyBtnClick, setIsCopyBtnClick] = useState(false);
  const code =
    props.activeTab === "request"
      ? props.widgetProps.curlRequestCommand
      : JSON.stringify(props.widgetProps.curlResponseCommand, null, 2);

  const handleRequestTabClick = () => {
    props.setActiveTab("request");
  };

  const handleResponseTabClick = () => {
    props.setActiveTab("response");
  };

  const handleCopyButtonClick = () => {
    copyToClipboard(code);
    setIsCopyBtnClick(true);
    setTimeout(() => setIsCopyBtnClick(false), 1000);
  }

  return (
    <>
      <div className="tab-container">
        <div
          className={`tab ${props.activeTab === "request" && "active"}`}
          onClick={handleRequestTabClick}
        >
          Request
        </div>
        <div
          className={`tab ${props.activeTab === "response" && "active"}`}
          onClick={handleResponseTabClick}
        >
          Response
        </div>
        <div onClick={handleCopyButtonClick} className="copy-icon">
          <CopyIcon className={`${isCopyBtnClick ? 'copy-btn-blink' : ''}`}></CopyIcon>
        </div>
      </div>
    </>
  );
};

const CodeDisplay: React.FC<{ widgetProps: Props; activeTab: string }> = (
  props
) => {
  const [copied, copyToClipboard] = useCopyToClipboard();
  const [isCopyBtnClick, setIsCopyBtnClick] = useState(false);
  const code =
    props.widgetProps.widgetType === WidgetType.Webhook
      ? JSON.stringify(props.widgetProps.webhookResponseJson, null, 2)
      : props.activeTab === "request"
        ? props.widgetProps.curlRequestCommand && Object.keys(props.widgetProps.curlRequestCommand).length ? props.widgetProps.curlRequestCommand : '{}'
        : JSON.stringify(props.widgetProps.curlResponseCommand, null, 2);

  const handleCopyButtonClick = () => {
    copyToClipboard(
      JSON.stringify(props.widgetProps.webhookResponseJson, null, 2)
    )
    setIsCopyBtnClick(true);
    setTimeout(() => setIsCopyBtnClick(false), 1000);
  }
  return (
    <div>
      {props.widgetProps.widgetType === WidgetType.Webhook && (
        <div
          onClick={handleCopyButtonClick}
          className="copy-icon"
        >
          <CopyIcon className={`${isCopyBtnClick ? 'copy-btn-blink' : ''}`}></CopyIcon>
        </div>
      )}
      <pre className="json-data" data-testid="response-code">{code}</pre>
    </div>
  );
};

export default RequestResponseViewer;
